import type { PaidTier } from './tiers';

/**
 * Feature gating — the user-facing COPY for each gate, and which tier unlocks it.
 *
 * The gates read capabilities from `useSubscription`, not this file:
 *   canPlan (Plus and above) — Strategy tab, multi-wallet allocation, per-wallet
 *                              Portfolio Value chart and Asset Allocation
 *   canTax  (Pro only)       — Tax tab in full
 * See config/tiers.ts for how a capability is derived from the Stripe price.
 *
 * FREE
 *  - Add / edit transactions (unlimited)
 *  - Portfolio tracking: holdings, values, P&L, allocation
 *  - Holdings table wallet filter (the table only; see walletView)
 *  - Market data
 *  - Cash-out goal card (Portfolio tab)
 *
 * PLUS — adds `canPlan`
 *  - Strategy tab in full: Compare, Split Buy, Split Sell, saved plans
 *  - Multi-wallet allocation per coin
 *  - Portfolio Value chart and Asset Allocation for one selected wallet
 *
 * PRO — adds `canTax`
 *  - Tax tab in full: disposals list, per-asset CGT breakdown, FY switcher,
 *    planned sales, CSV & PDF export
 *
 * Each gate carries its TIER as well as its message, so the upgrade modal can
 * offer the right plan. A gate that named the wrong tier would send someone to
 * buy a subscription that does not unlock what they just clicked.
 *
 * Sample/demo data is never counted — only the user's real data.
 */

export interface UpgradeGate {
  /** What to say in the upgrade modal. */
  message: string;
  /**
   * The tier that unlocks this. Omitted only by the generic CTA, which is not
   * about any particular feature and so offers both plans equally.
   */
  tier?: PaidTier;
}

/** Messages shown when a user hits a gate above their tier. */
export const FEATURE_GATES = {
  // ── Plus ──────────────────────────────────────────────────────────────────
  strategy: {
    tier: 'plus',
    message:
      'Plan your entries and exits before the market moves. Ladder your buys across price levels, stage your sells, and compare any two or more assets side by side. Available on Plus.',
  },
  multiWallet: {
    tier: 'plus',
    message:
      'Multi-wallet allocation per coin is a Plus feature. Upgrade to Plus to see how each coin is split across your wallets.',
  },
  walletView: {
    tier: 'plus',
    message:
      'Charting a single wallet is a Plus feature. Upgrade to Plus to see the Portfolio Value chart and Asset Allocation for each wallet on its own.',
  },

  // ── Pro ───────────────────────────────────────────────────────────────────
  cgtBreakdown: {
    tier: 'pro',
    message:
      'The full per-disposal CGT breakdown is a Pro feature. Upgrade to Pro to see every disposal, FIFO parcel, and the tax owed on each.',
  },
  fySwitcher: {
    tier: 'pro',
    message:
      'Reviewing other financial years is a Pro feature. Upgrade to Pro to switch between financial years.',
  },
  taxExports: {
    tier: 'pro',
    message:
      'Tax report exports (CSV & PDF) are a Pro feature. Upgrade to Pro to download your CGT report and transaction history.',
  },
  plannedSales: {
    tier: 'pro',
    message:
      'The tax planner, including saved estimates and harvest opportunities, is a Pro feature. Upgrade to Pro to plan future sales.',
  },
} as const satisfies Record<string, UpgradeGate>;

/**
 * Shown when the upgrade modal is opened from a plain "Upgrade" button (header /
 * settings) rather than after hitting a specific gate.
 *
 * No `tier`: the user has not asked for anything in particular, so the modal
 * shows both plans and lets them choose. Naming one here would quietly steer the
 * only moment where the choice is genuinely open.
 */
export const UPGRADE_CTA: UpgradeGate = {
  message:
    'Plus adds the planning tools: split buy and sell ladders, asset comparison, and a wallet-by-wallet breakdown. Pro adds everything in Plus, plus the Australian tax tools. Every disposal with its FIFO cost base, any financial year, and a CGT report for your accountant.',
};
